import {
  useState,
  cloneElement,
  isValidElement,
  memo,
  useEffect,
  useMemo
} from 'react'
import PropTypes from 'prop-types'

import {
  ItemContainer,
  ItemChevron,
  ItemTextContent,
  ItemTitle,
  ItemDescription,
  ItemBadge,
  ItemValue,
  ItemCount
} from '../styled-components'

import CollapsibleContent from './collapsible-content'

import { ChevronRight } from '../../../atoms/icons/essentials'
import BadgesComponent from '../../../atoms/badges'
import {
  badgesVariantOptions,
  badgeSizeOptions,
  badgeColorOptions
} from '../../../atoms/badges/constants'
import colorsOptions from '../../../fundamentals/colors-styles/constants'
import { chevronMoveDirectionOptions } from '../constants'

const CollapsibleValue = memo(({ value, color }) => (
  <ItemValue color={color}>{value}</ItemValue>
))

CollapsibleValue.displayName = 'CollapsibleValue'

const CollapsibleGroupItem = memo(
  ({
    defaultIsOpen = false,
    showChevronRight = false,
    showChevronLeft = true,
    chevronLeftIcon = null,
    chevronRightIcon = null,
    chevronMoveDirection = chevronMoveDirectionOptions.RIGHT_TO_DOWN,
    isOpen: isOpenProp = null,
    setIsOpen: setIsOpenProp,
    leftIcon = null,
    rightIcon = null,
    title = null,
    titleStyle = null,
    description = null,
    descriptionStyle = null,
    badgeValue = null,
    value = null,
    count = null,
    children = <></>,
    disabled = false,
    readOnly = false,
    borderBottom = true,
    borderFull = true,
    disableAnimation = false,
    hideValueWhenIsOpen = false,
    onToggleClick = () => {},
    activeWhenOpen = false,
    isSelected = false,
    ...props
  }) => {
    const [internalIsOpen, setInternalIsOpen] = useState(defaultIsOpen)

    const isOpen = isOpenProp ?? internalIsOpen
    const setIsOpen = setIsOpenProp ?? setInternalIsOpen

    const defineColor = useMemo(() => {
      if (disabled) {
        return colorsOptions.greys['grey-40']
      }
      if (readOnly) {
        return colorsOptions.greys['grey-70']
      }
      return undefined
    }, [disabled, readOnly])

    const LeadIcon = ({ icon }) => {
      if (isValidElement(icon)) {
        return cloneElement(icon, { stroke: defineColor })
      }
      return icon
    }

    const effectiveChevronLeft = showChevronRight ? false : showChevronLeft

    const handleToggle = () => {
      if (readOnly || disabled) return

      if (setIsOpen) {
        setIsOpen(!isOpen)
        onToggleClick(!isOpen)
      } else {
        setIsOpen(!isOpen)
      }
    }

    const handleHideValueWhenIsOpen = () => {
      if (hideValueWhenIsOpen) {
        return !isOpen
      }
      return true
    }

    const RenderChevron = ({ icon }) => {
      if (isValidElement(icon)) {
        return cloneElement(icon, { stroke: defineColor })
      }
      return <ChevronRight size="icon-s" stroke={defineColor} />
    }

    // Atualizar o estado interno quando defaultIsOpen mudar
    useEffect(() => {
      if (isOpenProp === null) {
        setIsOpen(defaultIsOpen)
      }
    }, [defaultIsOpen, isOpenProp, setIsOpen])

    return (
      <>
        <ItemContainer
          onClick={handleToggle}
          borderBottom={borderBottom}
          borderFull={borderFull}
          isOpen={isOpen}
          backgroundActive={activeWhenOpen && isOpen}
          isSelected={isSelected}
          {...props}
        >
          {effectiveChevronLeft && (
            <ItemChevron
              isOpen={isOpen}
              chevronMoveDirection={chevronMoveDirection}
            >
              <RenderChevron icon={chevronLeftIcon} />
            </ItemChevron>
          )}

          {leftIcon && <LeadIcon icon={leftIcon} />}

          <ItemTextContent>
            {title && (
              <ItemTitle
                color={defineColor}
                style={titleStyle}
                isSelected={isSelected}
              >
                {title}
              </ItemTitle>
            )}
            {description && (
              <ItemDescription color={defineColor} style={descriptionStyle}>
                {description}
              </ItemDescription>
            )}
          </ItemTextContent>

          {badgeValue && (
            <ItemBadge>
              <BadgesComponent
                variant={badgesVariantOptions.STRONG}
                size={badgeSizeOptions.MEDIUM}
                color={badgeColorOptions.DOCU_TOOLS_ORANGE}
              >
                {badgeValue}
              </BadgesComponent>
            </ItemBadge>
          )}

          {handleHideValueWhenIsOpen() && value && (
            <CollapsibleValue value={value} color={defineColor} />
          )}

          {count && (
            <ItemCount>
              <BadgesComponent
                variant={badgesVariantOptions.STRONG}
                size={badgeSizeOptions.MEDIUM}
                disabled={disabled || readOnly}
              >
                {count}
              </BadgesComponent>
            </ItemCount>
          )}

          {rightIcon && <LeadIcon icon={rightIcon} />}

          {showChevronRight && (
            <ItemChevron
              isOpen={isOpen}
              chevronMoveDirection={chevronMoveDirection}
            >
              <RenderChevron icon={chevronRightIcon} />
            </ItemChevron>
          )}
        </ItemContainer>

        <CollapsibleContent
          isOpen={isOpen}
          borderBottom={borderBottom}
          borderFull={borderFull}
          disableAnimation={disableAnimation}
        >
          {children}
        </CollapsibleContent>
      </>
    )
  }
)

CollapsibleGroupItem.displayName = 'CollapsibleGroupItem'
CollapsibleGroupItem.prototype = {
  defaultIsOpen: PropTypes.bool,
  showChevronRight: PropTypes.bool,
  showChevronLeft: PropTypes.bool,
  chevronLeftIcon: PropTypes.node,
  chevronRightIcon: PropTypes.node,
  chevronMoveDirection: PropTypes.oneOf(
    Object.values(chevronMoveDirectionOptions)
  ),
  isOpen: PropTypes.bool,
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  title: PropTypes.string,
  titleStyle: PropTypes.object,
  badgeValue: PropTypes.string,
  value: PropTypes.string,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  borderBottom: PropTypes.bool,
  borderFull: PropTypes.bool,
  disableAnimation: PropTypes.bool,
  hideValueWhenIsOpen: PropTypes.bool,
  activeWhenOpen: PropTypes.bool,
  onToggleClick: PropTypes.func,
  isSelected: PropTypes.bool
}

export default CollapsibleGroupItem
